<script setup lang="ts">
/**
 * Branded confirm dialog host — rendered once at the App root (next to
 * <ToastShelf />). Consumes the shared `useConfirm()` registry: when a
 * component calls `confirm({...})`, a pending request appears here and
 * this dialog renders. Resolving (accept/cancel) clears it.
 *
 * Focus-managed (the confirm button gets focus on open) and Esc cancels,
 * matching RequestAiDialog's modal conventions. Replaces the unbranded,
 * un-focus-managed native `window.confirm()`.
 */
import { nextTick, onBeforeUnmount, ref, watch } from "vue";
import { useConfirm } from "../composables/useConfirm";

const { pending, accept, cancel } = useConfirm();

const confirmBtn = ref<HTMLButtonElement | null>(null);

function onKeydown(e: KeyboardEvent): void {
  if (!pending.value) return;
  if (e.key === "Escape") {
    e.preventDefault();
    cancel();
  } else if (e.key === "Enter") {
    e.preventDefault();
    accept();
  }
}

watch(
  () => pending.value?.id,
  (id) => {
    if (id != null) {
      document.addEventListener("keydown", onKeydown);
      nextTick(() => confirmBtn.value?.focus());
    } else {
      document.removeEventListener("keydown", onKeydown);
    }
  },
);

onBeforeUnmount(() => document.removeEventListener("keydown", onKeydown));
</script>

<template>
  <Teleport to="body">
    <div
      v-if="pending"
      class="confirm__backdrop"
      @click.self="cancel"
    >
      <div
        class="confirm"
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="confirm-title"
        aria-describedby="confirm-message"
      >
        <h2 id="confirm-title" class="confirm__title">{{ pending.title }}</h2>
        <p id="confirm-message" class="confirm__message">{{ pending.message }}</p>
        <div class="confirm__actions">
          <button type="button" class="btn" @click="cancel">
            {{ pending.cancelLabel ?? "Cancel" }}
          </button>
          <button
            ref="confirmBtn"
            type="button"
            :class="['btn', pending.danger ? 'btn--danger' : 'btn--primary']"
            @click="accept"
          >
            {{ pending.confirmLabel ?? "Confirm" }}
          </button>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.confirm__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 29, 46, 0.55);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1200;
  padding: var(--space-4);
}
.confirm {
  background: var(--color-surface);
  border-radius: var(--radius-lg, 12px);
  max-width: 440px;
  width: 100%;
  padding: var(--space-5);
  box-shadow: 0 12px 48px rgba(15, 29, 46, 0.3);
}
.confirm__title {
  font-size: 1.15rem;
  font-weight: 700;
  margin: 0 0 var(--space-2);
  color: var(--color-ink);
}
.confirm__message {
  margin: 0 0 var(--space-4);
  color: var(--color-ink);
  line-height: 1.5;
}
.confirm__actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--space-2);
}
</style>
